import { DotsThreeVertical } from "@phosphor-icons/react";
import { useMemo } from "react";

import DesktopActions from "common/components/DesktopActions";
import { ProxyLocationTag } from "common/components/Proxy";
import SimpleDataTable from "common/components/SimpleDataTable";
import { Button } from "common/components/ui/Button";
import { decodeProxyUrl } from "common/helpers/utils";
import type { CustomProxy } from "common/types";

import { PROXY_ACTIONS } from "../../../constants";
import ProxyActions from "../ProxyActions";

import type { ColumnDef } from "@tanstack/react-table";

export default function ProxiesDesktopTable({
  proxies,
}: {
  proxies: CustomProxy[];
}) {
  const columns = useMemo<ColumnDef<CustomProxy>[]>(
    () => [
      {
        id: "name",
        header: "Proxy name",
        cell: ({ row }) => (
          <span className="text-sm-bold">{row.original.name}</span>
        ),
      },
      {
        id: "url",
        header: "URL",
        cell: ({ row }) => {
          const { protocol, address } = decodeProxyUrl(row.original.url);

          return (
            <>
              {protocol}://{address}
            </>
          );
        },
      },
      {
        id: "authentication",
        header: "Authentication",
        cell: ({ row }) => {
          const { username, password } = decodeProxyUrl(row.original.url);
          const hasAuth = !!username && !!password;

          return hasAuth ? "***************" : "-";
        },
      },
      {
        id: "location",
        header: "Location",
        cell: ({ row }) => {
          const { location, valid: isValid } = row.original;

          return (
            <ProxyLocationTag
              location={
                location && {
                  countryCode: location.countryISO,
                  name: location.city || location.country,
                }
              }
              size="md"
              isValid={isValid}
            />
          );
        },
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <DesktopActions
            triggerElement={
              <Button intent="iconOnly" variant="secondary-black" size="sm">
                <DotsThreeVertical />
              </Button>
            }
            editOptions={PROXY_ACTIONS}
            getDialogSize={(action) => (action === "delete" ? "md" : "lg")}
          >
            {(action, onClose) => (
              <ProxyActions
                proxy={row.original}
                action={action}
                onClose={onClose}
                onBack={onClose}
              />
            )}
          </DesktopActions>
        ),
      },
    ],
    [],
  );

  return (
    <SimpleDataTable
      columns={columns}
      data={proxies}
      localStorageId="proxies"
    />
  );
}
